import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '@app/services/api';
import { parentCandidatesZql } from '@modules/database/work-items/frontend/components/BulkChangeModal/bulkRuns.mjs';

// How many candidates the list shows — Bulk Edit's Set parent lists as many.
const PARENT_LIST_MAX = 50;

function ParentEditor({ value, disabled, onAutoSave, projectKey, parentIssueTypes, currentIssueKey, currentJQL, expression, anchorKey, excludeKeys }) {
  const [search, setSearch] = useState('');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const parentTypesRef = useRef(parentIssueTypes);
  parentTypesRef.current = parentIssueTypes;
  const currentJqlRef = useRef(currentJQL);
  currentJqlRef.current = currentJQL;
  const expressionRef = useRef(expression);
  expressionRef.current = expression;
  const anchorKeyRef = useRef(anchorKey);
  anchorKeyRef.current = anchorKey;
  const excludeKeysRef = useRef(excludeKeys);
  excludeKeysRef.current = excludeKeys;

  // THE SAME SEARCH AS BULK EDIT'S SET PARENT (parentCandidatesZql): the
  // types the hierarchy allows, in the project, newest first — and what is
  // typed matches the key or the summary, so "495" finds SANDBOX-495. The
  // old picker search (/api/issue-picker) matched a key only from its start,
  // showed ten, and ordered them by internal id as text. The server still
  // has the last word on save (parent-change.js).
  const searchSeq = useRef(0);
  const fetchIssues = useCallback(async (query) => {
    const seq = ++searchSeq.current;
    setLoading(true);
    try {
      // No project given (a hierarchy that hangs this type by a link): the
      // item's own project, as the old picker search always used — not
      // every project's parents of that type.
      const scopeProject = projectKey
        || (currentIssueKey && currentIssueKey.includes('-') ? currentIssueKey.slice(0, currentIssueKey.lastIndexOf('-')) : undefined);
      const zql = parentCandidatesZql({
        projectKey: scopeProject,
        types: parentTypesRef.current,
        text: query,
        extraZql: currentJqlRef.current || '',
      });
      const result = await api.searchIssues(zql, 'summary,issuetype,status', PARENT_LIST_MAX, null, 0, true);
      if (seq !== searchSeq.current) return; // a later search has the list
      // Never the item itself, nor anything under it (that would be a loop).
      const excludeSet = new Set([currentIssueKey, ...(excludeKeysRef.current || [])].filter(Boolean));
      setIssues((result?.issues || [])
        .filter(i => i?.key && !excludeSet.has(i.key))
        .map(i => ({
          key: i.key,
          summary: i.fields?.summary || '',
          iconUrl: i.fields?.issuetype?.iconUrl || null,
          issueTypeName: i.fields?.issuetype?.name || '',
        })));
    } catch (err) {
      if (seq !== searchSeq.current) return;
      console.error('Failed to search work items:', err);
      setIssues([]);
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  }, [currentIssueKey, projectKey]);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
    fetchIssues('');
  }, [fetchIssues]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchIssues(query), 300);
  };

  const handleSelect = useCallback((issueKey) => {
    if (disabled) return;
    if (onAutoSave) onAutoSave(issueKey);
  }, [disabled, onAutoSave]);

  const handleKeyDown = useCallback((e) => {
    const totalItems = issues.length + 1; // +1 for "None" option
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex === 0) {
        handleSelect(null);
      } else if (highlightedIndex > 0 && highlightedIndex <= issues.length) {
        handleSelect(issues[highlightedIndex - 1].key);
      }
    }
  }, [issues, highlightedIndex, handleSelect]);

  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <div className="field-editor-user-wrapper">
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder="Search parent work items..."
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {loading ? (
        <div className="field-editor-loading">Searching...</div>
      ) : (
        <div className="field-editor-options-list" ref={listRef}>
          <div
            className={`field-editor-option ${!value ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
            onClick={() => handleSelect(null)}
            onMouseEnter={() => setHighlightedIndex(0)}
          >
            <span className="field-editor-option-label" style={{ fontStyle: 'italic' }}>None</span>
          </div>
          {issues.map((issue, idx) => {
            const isSelected = value === issue.key;
            const itemIndex = idx + 1;
            return (
              <div
                key={issue.key}
                className={`field-editor-option ${isSelected ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(issue.key)}
                onMouseEnter={() => setHighlightedIndex(itemIndex)}
              >
                {issue.iconUrl && (
                  <img src={api.getJiraImageUrl(issue.iconUrl)} alt="" width="16" height="16" style={{ borderRadius: 2, flexShrink: 0 }} />
                )}
                <span className="field-editor-option-label">
                  <strong>{issue.key}</strong> {issue.summary}
                </span>
                {isSelected && <span className="field-editor-option-check">&#10003;</span>}
              </div>
            );
          })}
          {issues.length === 0 && !loading && (
            <div className="field-editor-loading" style={{ fontStyle: 'italic' }}>No matching work items</div>
          )}
        </div>
      )}
    </div>
  );
}

export default ParentEditor;
